import { Button, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import styles from './Navigation.module.scss';
import { useProjectPermissions } from '../../hooks/useProjectPermissions';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { areaIcons } from '@/constants/areaIcons';
import { routes } from '@/constants/routes';
import { uiIcons } from '@/constants/uiIcons';

export const Navigation = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation('projectManagement');
  const location = useLocation();

  // Get available tabs based on permissions
  const { availableTabs, isLoading } = useProjectPermissions(projectId);

  // Map section names to their actual URLs using centralized route helpers
  const sectionUrls = {
    planning: routes.projects.planning(projectId),
    team: routes.projects.team(projectId),
    financials: routes.projects.financials(projectId),
    billing: routes.projects.billing(projectId),
    settings: routes.projects.settings(projectId),
  };

  // Determine active section from URL
  const getActiveSection = () => {
    const path = location.pathname;

    // Check new URL patterns first
    if (path.includes('/planning')) return 'planning';
    if (path.includes('/team')) return 'team';
    if (path.includes('/financials')) return 'financials';
    if (path.includes('/billing')) return 'billing';

    // Check legacy patterns (for when internally navigates)
    if (path.includes('/Resource/Group/')) return 'team';
    if (path.includes('/ProjectSubContract/')) return 'financials';
    if (path.includes('/Billing/Index/')) return 'billing';
    if (path.includes('/Plan/Index/') || path.includes('/Plan/Task/'))
      return 'planning';

    return null;
  };

  const activeSection = getActiveSection();
  return (
    <>
      <Stack
        component="nav"
        direction="row"
        alignItems="center"
        className={styles.ButtonGroup}
      >
        {isLoading ? (
          <Skeleton
            variant="pilled"
            width={200}
            height={36}
            data-automation-id="project-management-menu-skeleton"
          />
        ) : (
          <>
            {availableTabs.planning && (
              <Button
                component={NavLink}
                to={sectionUrls.planning}
                variant={activeSection === 'planning' ? 'contained' : 'text'}
                color="secondary"
                startIcon={<Icon name={areaIcons.projects} />}
                size="medium"
                data-automation-id="project-management-menu-item-planning"
              >
                {t('menuItem.Planning')}
              </Button>
            )}
            {availableTabs.financials && (
              <Button
                component={NavLink}
                to={sectionUrls.financials}
                variant={activeSection === 'financials' ? 'contained' : 'text'}
                color="secondary"
                startIcon={<Icon name={areaIcons.projectFinancials} />}
                size="medium"
                data-automation-id="project-management-menu-item-financials"
              >
                {t('menuItem.Financials')}
              </Button>
            )}
            {availableTabs.team && (
              <Button
                component={NavLink}
                to={sectionUrls.team}
                variant={activeSection === 'team' ? 'contained' : 'text'}
                color="secondary"
                startIcon={<Icon name={areaIcons.projectTeam} />}
                size="medium"
                data-automation-id="project-management-menu-item-team"
              >
                {t('menuItem.Team')}
              </Button>
            )}
            {availableTabs.billing && (
              <Button
                component={NavLink}
                to={sectionUrls.billing}
                variant={activeSection === 'billing' ? 'contained' : 'text'}
                color="secondary"
                startIcon={<Icon name={areaIcons.projectBilling} />}
                size="medium"
                data-automation-id="project-management-menu-item-billing"
              >
                {t('menuItem.Billing')}
              </Button>
            )}
          </>
        )}
      </Stack>
      {isLoading ? (
        <Skeleton
          variant="circular"
          width={36}
          height={36}
          data-automation-id="project-management-menu-skeleton"
        />
      ) : (
        availableTabs.settings && (
          <IconButton
            tooltip={t('menuItem.Settings')}
            iconName={uiIcons.controls.settings}
            variant="outlined"
            component={NavLink}
            to={sectionUrls.settings}
            data-automation-id="project-management-menu-item-settings"
          />
        )
      )}
    </>
  );
};
